import React, { useEffect, useRef, useState } from 'react';
import { ChartData } from '../types';

interface ChartWidgetProps {
  chart: ChartData;
}

export const ChartWidget: React.FC<ChartWidgetProps> = ({ chart }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState<number>(300);
  const heightRef = useRef<number>(300);

  useEffect(() => {
    if (!iframeRef.current || !chart.code) return;

    const iframe = iframeRef.current;

    // 使用 srcdoc 属性来设置 iframe 内容，避免跨域问题
    // 构建iframe内容
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      background: transparent;
      padding: 0;
      margin: 0;
      overflow: hidden;
    }

    /* CSS Variables - 匹配设计系统 */
    :root {
      --color-background-primary: #ffffff;
      --color-background-secondary: #f5f5f5;
      --color-background-tertiary: #fafafa;
      --color-text-primary: #1a1a1a;
      --color-text-secondary: #666666;
      --color-text-tertiary: #999999;
      --color-border-tertiary: rgba(0, 0, 0, 0.08);
      --color-border-secondary: rgba(0, 0, 0, 0.15);
      --color-border-primary: rgba(0, 0, 0, 0.2);
      --color-text-info: #185fa5;
      --color-text-success: #3b6d11;
      --color-text-warning: #854f0b;
      --color-text-danger: #a32d2d;
      --color-background-info: #e6f1fb;
      --color-background-success: #eaf3de;
      --color-background-warning: #faeeda;
      --color-background-danger: #fcebeb;
      --border-radius-md: 8px;
      --border-radius-lg: 12px;
      --border-radius-xl: 16px;
      --font-sans: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      --font-serif: Georgia, 'Times New Roman', serif;
      --font-mono: 'SF Mono', Monaco, 'Cascadia Code', monospace;
    }

    @media (prefers-color-scheme: dark) {
      :root {
        --color-background-primary: #1a1a1a;
        --color-background-secondary: #2a2a2a;
        --color-background-tertiary: #1f1f1f;
        --color-text-primary: #ffffff;
        --color-text-secondary: #b0b0b0;
        --color-text-tertiary: #808080;
        --color-border-tertiary: rgba(255, 255, 255, 0.1);
        --color-border-secondary: rgba(255, 255, 255, 0.2);
        --color-border-primary: rgba(255, 255, 255, 0.3);
        --color-text-info: #85b7eb;
        --color-text-success: #97c459;
        --color-text-warning: #ef9f27;
        --color-text-danger: #f09595;
        --color-background-info: #0c447c;
        --color-background-success: #27500a;
        --color-background-warning: #633806;
        --color-background-danger: #791f1f;
      }
    }

    /* SVG 颜色类 - Light Mode */
    .c-purple { fill: #EEEDFE; stroke: #534AB7; }
    .c-teal { fill: #E1F5EE; stroke: #0F6E56; }
    .c-coral { fill: #FAECE7; stroke: #993C1D; }
    .c-pink { fill: #FBEAF0; stroke: #993556; }
    .c-gray { fill: #F1EFE8; stroke: #5F5E5A; }
    .c-blue { fill: #E6F1FB; stroke: #185FA5; }
    .c-green { fill: #EAF3DE; stroke: #3B6D11; }
    .c-amber { fill: #FAEEDA; stroke: #854F0B; }
    .c-red { fill: #FCEBEB; stroke: #A32D2D; }

    /* SVG 文本类 */
    .t { font-family: var(--font-sans); font-size: 14px; font-weight: 400; fill: var(--color-text-primary); }
    .ts { font-family: var(--font-sans); font-size: 12px; font-weight: 400; fill: var(--color-text-secondary); }
    .th { font-family: var(--font-sans); font-size: 14px; font-weight: 500; fill: var(--color-text-primary); }

    /* 颜色容器内的文本 - Light Mode */
    .c-purple .t, .c-purple .th { fill: #3C3489; }
    .c-purple .ts { fill: #534AB7; }
    .c-teal .t, .c-teal .th { fill: #085041; }
    .c-teal .ts { fill: #0F6E56; }
    .c-coral .t, .c-coral .th { fill: #712B13; }
    .c-coral .ts { fill: #993C1D; }
    .c-pink .t, .c-pink .th { fill: #72243E; }
    .c-pink .ts { fill: #993556; }
    .c-gray .t, .c-gray .th { fill: #444441; }
    .c-gray .ts { fill: #5F5E5A; }
    .c-blue .t, .c-blue .th { fill: #0C447C; }
    .c-blue .ts { fill: #185FA5; }
    .c-green .t, .c-green .th { fill: #27500A; }
    .c-green .ts { fill: #3B6D11; }
    .c-amber .t, .c-amber .th { fill: #633806; }
    .c-amber .ts { fill: #854F0B; }
    .c-red .t, .c-red .th { fill: #791F1F; }
    .c-red .ts { fill: #A32D2D; }

    @media (prefers-color-scheme: dark) {
      /* SVG 颜色类 - Dark Mode */
      .c-purple { fill: #3C3489; stroke: #CECBF6; }
      .c-teal { fill: #085041; stroke: #9FE1CB; }
      .c-coral { fill: #712B13; stroke: #F5C4B3; }
      .c-pink { fill: #72243E; stroke: #F4C0D1; }
      .c-gray { fill: #444441; stroke: #D3D1C7; }
      .c-blue { fill: #0C447C; stroke: #B5D4F4; }
      .c-green { fill: #27500A; stroke: #C0DD97; }
      .c-amber { fill: #633806; stroke: #FAC775; }
      .c-red { fill: #791F1F; stroke: #F7C1C1; }

      /* 颜色容器内的文本 - Dark Mode */
      .c-purple .t, .c-purple .th { fill: #EEEDFE; }
      .c-purple .ts { fill: #CECBF6; }
      .c-teal .t, .c-teal .th { fill: #E1F5EE; }
      .c-teal .ts { fill: #9FE1CB; }
      .c-coral .t, .c-coral .th { fill: #FAECE7; }
      .c-coral .ts { fill: #F5C4B3; }
      .c-pink .t, .c-pink .th { fill: #FBEAF0; }
      .c-pink .ts { fill: #F4C0D1; }
      .c-gray .t, .c-gray .th { fill: #F1EFE8; }
      .c-gray .ts { fill: #D3D1C7; }
      .c-blue .t, .c-blue .th { fill: #E6F1FB; }
      .c-blue .ts { fill: #B5D4F4; }
      .c-green .t, .c-green .th { fill: #EAF3DE; }
      .c-green .ts { fill: #C0DD97; }
      .c-amber .t, .c-amber .th { fill: #FAEEDA; }
      .c-amber .ts { fill: #FAC775; }
      .c-red .t, .c-red .th { fill: #FCEBEB; }
      .c-red .ts { fill: #F7C1C1; }
    }

    /* 通用样式 */
    .box { fill: var(--color-background-secondary); stroke: var(--color-border-tertiary); }
    .node { cursor: pointer; }
    .node:hover { opacity: 0.8; }
    .arr { stroke: var(--color-text-tertiary); stroke-width: 1.5; fill: none; }
    .leader { stroke: var(--color-text-tertiary); stroke-width: 0.5; stroke-dasharray: 2,2; }

    /* 容器 */
    .is_container {
      width: 100%;
      display: block;
    }

    /* 输入控件样式 */
    input[type="range"] {
      -webkit-appearance: none;
      width: 100%;
      height: 4px;
      background: var(--color-border-secondary);
      border-radius: 2px;
      outline: none;
    }
    input[type="range"]::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: 18px;
      height: 18px;
      background: var(--color-background-primary);
      border: 0.5px solid var(--color-border-secondary);
      border-radius: 50%;
      cursor: pointer;
    }
    input[type="range"]::-moz-range-thumb {
      width: 18px;
      height: 18px;
      background: var(--color-background-primary);
      border: 0.5px solid var(--color-border-secondary);
      border-radius: 50%;
      cursor: pointer;
    }

    button {
      background: var(--color-background-secondary);
      border: 0.5px solid var(--color-border-secondary);
      border-radius: var(--border-radius-md);
      padding: 8px 16px;
      font-size: 14px;
      cursor: pointer;
      color: var(--color-text-primary);
    }
    button:hover {
      background: var(--color-background-tertiary);
    }
    button:active {
      transform: scale(0.98);
    }

    /* 卡片样式 */
    .card {
      background: var(--color-background-primary);
      border: 0.5px solid var(--color-border-tertiary);
      border-radius: var(--border-radius-lg);
      padding: 1rem 1.25rem;
    }

    /* 指标卡片 */
    .metric-card {
      background: var(--color-background-secondary);
      border-radius: var(--border-radius-md);
      padding: 1rem;
    }

    /* 响应式 */
    @media (max-width: 680px) {
      svg { max-width: 100%; }
    }
  </style>
</head>
<body>
  <div class="is_container">
    ${chart.code}
  </div>
  <script>
    // 通知父窗口内容高度变化
    function notifyHeight() {
      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: 'chart-height', height: height }, '*');
    }

    // 初始通知
    window.addEventListener('load', notifyHeight);

    // 监听内容变化
    const observer = new MutationObserver(notifyHeight);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
  </script>
</body>
</html>
    `;

    // 使用 srcdoc 属性设置内容，避免跨域访问问题
    iframe.srcdoc = htmlContent;

    // 监听来自iframe的消息
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'chart-height') {
        const newHeight = Math.max(event.data.height + 20, 200);
        // 只有高度变化超过阈值时才更新，避免频繁渲染导致的闪烁
        if (Math.abs(newHeight - heightRef.current) > 5) {
          heightRef.current = newHeight;
          setHeight(newHeight);
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [chart.code]);

  return (
    <div className="chart-widget">
      <div className="chart-title">{chart.title}</div>
      <iframe
        ref={iframeRef}
        title={chart.title}
        className="chart-iframe"
        style={{
          width: chart.width || '100%',
          height: `${height}px`,
          border: 'none',
          background: 'transparent',
        }}
        sandbox="allow-scripts allow-same-origin"
      />
    </div>
  );
};
